import React, { forwardRef, useEffect, useState } from 'react';

import DOMPurify from 'dompurify';

import { getTenantLogoUrl } from '../../services/tenantLogoApi';

import { formatProjectDate, getStatusLabel } from './projectsData';

import { buildProjectPreviewSections } from './projectScopePreviewModel';



const thStyle =

  'border border-gray-800 bg-sky-100 px-2 py-1.5 text-left text-[11px] font-bold text-gray-900';

const tdStyle = 'border border-gray-800 px-2 py-1.5 align-top text-[11px] text-gray-900';

const sectionTitleStyle =

  'border border-gray-800 bg-blue-100 px-2 py-1.5 text-left text-[12px] font-bold uppercase text-gray-900';

const subTitleStyle =

  'border border-gray-800 bg-gray-100 px-2 py-1.5 text-left text-[11px] font-bold text-gray-900';



const sanitizeHtml = (value) =>

  DOMPurify.sanitize(String(value || ''), { USE_PROFILES: { html: true } });



const FieldTable = ({ fields }) => (

  <table className="w-full border-collapse">

    <thead>

      <tr>

        <th className={`${thStyle} w-[34%]`}>Field</th>

        <th className={thStyle}>Details</th>

      </tr>

    </thead>

    <tbody>

      {fields.map((field) => (

        <tr key={field.id}>

          <td className={`${tdStyle} font-semibold`}>{field.label}</td>

          {field.isHtml ? (

            <td

              className={`${tdStyle} [&_p]:m-0 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-blue-700`}

              dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.value) }}

            />

          ) : (

            <td className={`${tdStyle} whitespace-pre-wrap`}>{field.value}</td>

          )}

        </tr>

      ))}

    </tbody>

  </table>

);



const ProjectScopeDocument = forwardRef(

  ({ project, sections: sectionsProp, logoUrl: logoUrlProp = undefined }, ref) => {

    const sections =

      sectionsProp ||

      buildProjectPreviewSections(project?.scopeSheet);

    const scopeDate = formatProjectDate(project?.scopeDate);



    const [tenantLogoUrl, setTenantLogoUrl] = useState(

      logoUrlProp === undefined ? null : logoUrlProp

    );

    const [logoReady, setLogoReady] = useState(logoUrlProp !== undefined);



    useEffect(() => {

      if (logoUrlProp !== undefined) {

        setTenantLogoUrl(logoUrlProp);

        setLogoReady(true);

        return undefined;

      }



      let cancelled = false;

      setLogoReady(false);



      (async () => {

        try {

          const url = await getTenantLogoUrl();

          if (!cancelled) {

            setTenantLogoUrl(url);

          }

        } catch (error) {

          console.warn('[ProjectScope] Failed to load tenant logo:', error);

          if (!cancelled) {

            setTenantLogoUrl(null);

          }

        } finally {

          if (!cancelled) {

            setLogoReady(true);

          }

        }

      })();



      return () => {

        cancelled = true;

      };

    }, [logoUrlProp]);



    return (

      <div

        ref={ref}

        data-project-scope-pdf="true"

        data-tenant-logo-ready={logoReady ? '1' : '0'}

        className="mx-auto bg-white text-gray-900 shadow-xl"

        style={{

          width: '210mm',

          minHeight: '297mm',

          padding: '10mm',

          boxSizing: 'border-box',

          fontFamily: 'Arial, Helvetica, sans-serif',

        }}

      >

        {/* Header block — same bordered table language as Interior Quotation */}

        <table className="mb-4 w-full border-collapse">

          <tbody>

            <tr>

              <td className={`${tdStyle} w-1/5 font-bold`}>Date</td>

              <td className={`${tdStyle} w-2/5`}>

                {scopeDate === '—' ? '—' : scopeDate}

              </td>

              <td

                className={`${tdStyle} text-center align-middle`}

                rowSpan={4}

                data-tenant-logo="1"

              >

                <div className="flex flex-col items-center justify-center gap-2 px-2 py-1">

                  {tenantLogoUrl ? (

                    <img

                      src={tenantLogoUrl}

                      alt="Organization logo"

                      className="max-h-16 max-w-[140px] object-contain"

                      onLoad={() => setLogoReady(true)}

                      onError={() => setLogoReady(true)}

                    />

                  ) : null}

                  <span className="text-xl font-bold tracking-wide">

                    ULTIMATE SCOPE SHEET

                  </span>

                </div>

              </td>

            </tr>

            <tr>

              <td className={`${tdStyle} font-bold`}>Client</td>

              <td className={tdStyle}>{project?.clientName || '—'}</td>

            </tr>

            <tr>

              <td className={`${tdStyle} font-bold`}>Project</td>

              <td className={tdStyle}>{project?.name || '—'}</td>

            </tr>

            <tr>

              <td className={`${tdStyle} font-bold`}>Status</td>

              <td className={tdStyle}>

                {getStatusLabel(project?.status) || project?.status || '—'}

              </td>

            </tr>

            <tr>

              <td className={`${tdStyle} font-bold`}>Address</td>

              <td className={tdStyle} colSpan={2}>

                {project?.location || '—'}

              </td>

            </tr>

          </tbody>

        </table>



        {!sections.length ? (

          <p className="text-sm text-gray-600">No scope sheet data filled.</p>

        ) : (

          <div className="flex flex-col gap-4">

            {sections.map((section) => (

              <section key={section.id}>

                <div className={sectionTitleStyle}>

                  Section {section.letter} — {section.title}

                </div>



                {section.type === 'balconyList'

                  ? section.balconies.map((balcony) => (

                      <div key={balcony.id} className="mt-2">

                        <div className={subTitleStyle}>{balcony.title}</div>

                        {balcony.groups.map((group) => (

                          <div key={group.id} className="mt-1">

                            <div className={`${subTitleStyle} bg-sky-50`}>

                              {group.title}

                            </div>

                            <FieldTable fields={group.fields} />

                          </div>

                        ))}

                      </div>

                    ))

                  : section.subsections.map((subsection) => (

                      <div key={subsection.id} className="mt-2">

                        <div className={subTitleStyle}>{subsection.title}</div>

                        <FieldTable fields={subsection.fields} />

                      </div>

                    ))}

              </section>

            ))}

          </div>

        )}

      </div>

    );

  }

);



ProjectScopeDocument.displayName = 'ProjectScopeDocument';



export default ProjectScopeDocument;


